import type { Zoned } from '../data/airportTime';
import type { CharterQuoteBody } from './schemas';

export type Status = 'active' | 'inactive';

export type Admin = {
  id: string;
  email: string;
  fullName: string;
};

export type Operator = {
  id: string;
  name: string;
  dba: string | null;
  email: string;
  address: string | null;
  phone: string | null;
  status: Status;
  createdAt: string;
  updatedAt: string;
  _count?: { aircraft: number };
};

export const AIRCRAFT_CATEGORIES = [
  'turbo_prop',
  'very_light_jet',
  'light_jet',
  'super_light_jet',
  'midsize_jet',
  'super_midsize_jet',
  'heavy_jet',
  'ultra_long_range',
  'vip_airliner',
] as const;

export type AircraftCategory = (typeof AIRCRAFT_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<AircraftCategory, string> = {
  turbo_prop:        'Turbo prop',
  very_light_jet:    'Very light jet',
  light_jet:         'Light jet',
  super_light_jet:   'Super light jet',
  midsize_jet:       'Midsize jet',
  super_midsize_jet: 'Super midsize jet',
  heavy_jet:         'Heavy jet',
  ultra_long_range:  'Ultra-long range',
  vip_airliner:      'VIP airliner',
};

export type PeakUpliftMultiplier = {
  id?: string;
  fromDate: string;
  toDate: string;
  multiplier: number | string;
};

export type Aircraft = {
  id: string;
  operatorId: string;
  operator?: { id: string; name: string };
  tailNumber: string;
  modelName: string;
  category: AircraftCategory;
  seatingCapacity: number;
  yearOfManufacture: number | null;
  yearOfRefurbishment: number | null;
  ownersApproval?: boolean; // SHAR-197/198
  // SHAR-442 — eligibility fields are optional (draft aircraft saved Inactive).
  maxRangeNm: number | null;
  cruiseSpeedKnots: number | null;
  homebaseAirportCode: string | null;
  homebaseAirportName: string | null;
  maxFerryDistanceNm: number | null;
  // nullable in the database and the spec (SHAR-1211 pin): a draft aircraft may have none
  confirmationDeadlineDays: number | null;
  // SHAR-458 — true when this aircraft's homebase airport no longer exists.
  homebaseMissing?: boolean;
  cancellationPolicy: string | null;
  mainPhotoUrl: string | null;
  photos?: { id: string; url: string; sortOrder: number }[];
  blackoutRanges?: { id: string; fromDate: string; toDate: string; reason: string | null }[];
  status: Status;

  // Prices block (SHAR-12)
  currencyCode: string | null;
  occupiedHourlyRate: string | number | null;
  ferryHourlyRate: string | number | null;
  contingencyTimeMin: number | null;
  minSectorTimeHours: string | number | null;
  minTripRevenue: string | number | null;
  peakUpliftMultipliers?: PeakUpliftMultiplier[];
};

// Subset of ISO 4217 — currencies of regions that run commercial private
// aviation. Used for the typeahead in the aircraft form.
export const AVIATION_CURRENCIES: { code: string; name: string }[] = [
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'Pound Sterling' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'RUB', name: 'Russian Rouble' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'ZAR', name: 'South African Rand' },
];

export type AirportHit = {
  /** SHAR-1188 — IANA zone; absent from an older API. */
  timezone?: string;
  iataCode: string | null;
  icaoCode: string;
  name: string;
  city: string | null;
  country: string;
  countryCode: string;
  lat: number | null;
  lng: number | null;
};

// ─── Airports management (SHAR-204..210) ──────────────────────────────
// SHAR-498/500/501 — removal is a SOFT delete: the row survives with
// status 'removed' so existing bookings and the search exception can still
// resolve it. The admin list can filter for it, so the type has to allow it.
export type AirportStatus = 'active' | 'inactive' | 'removed';

export type Airport = {
  id: string;
  icaoCode: string;
  iataCode: string | null;
  name: string;
  city: string | null;
  country: string;
  countryCode: string;
  isoRegion: string | null;
  lat: number | null;
  lng: number | null;
  /** SHAR-1188 — IANA time zone, e.g. Europe/London. */
  timezone: string;
  /** SHAR-1192 — the SHAR-1188 migration guessed this zone; absent from an older API. */
  timezoneGuessed?: boolean;
  /** How it was guessed: "nearest:EGKK", "country"… */
  timezoneGuess?: string | null;
  status: AirportStatus;
  fboName: string | null;
  fboAddress: string | null;
  fboPhone: string | null;
  fboEmail: string | null;
  runwayLengthFt: number | null;
  openingHours: string | null;
  generalNotes: string | null;
  // SHAR-458 — active aircraft that use this airport as homebase (they get
  // deactivated on delete). Present on admin list/detail responses.
  homebaseAircraftCount?: number;
};

export type AirportPage = {
  data: Airport[];
  total: number;
  page: number;
  pageSize: number;
};

// ─── Charters (SHAR Block 4) ──────────────────────────────────────────
export type CharterRequestStatus =
  | 'pending'
  | 'quotes_received'
  | 'cancelled'
  | 'booked';
export type CharterQuoteStatus =
  | 'created'
  | 'pending'
  | 'expired'
  | 'booked'
  | 'declined'
  | 'cancelled';

/** SHAR-614 — which details of a quote differ from its request (see the API's quote-alternatives.ts). */
export type QuoteAlternatives = {
  operator: boolean;
  aircraft: boolean;
  route: boolean;
  origin: boolean;
  destination: boolean;
  departure: boolean;
  duration: boolean;
  arrival: boolean;
  any: boolean;
};

export type CharterQuote = {
  /** SHAR-1192 — on the airport's clock; absent from an API before SHAR-1189. */
  departureZoned?: Zoned | null;
  arrivalZoned?: Zoned | null;
  id: string;
  operatorId: string | null;
  operatorName: string | null;
  aircraftId: string | null;
  tailNumber: string | null;
  // SHAR-599/608 — aircraft particulars carried on the quote row.
  modelName: string | null;
  category: AircraftCategory | null;
  seatingCapacity: number | null;
  yearOfManufacture: number | null;
  yearOfRefurbishment: number | null;
  originCode: string;
  destCode: string;
  originCity: string | null;
  destCity: string | null;
  originCountry: string | null;
  destCountry: string | null;
  departureDateTime: string;
  arrivalDateTime: string | null;
  flightDurationHours: number | null;
  priceGbp: number;
  // SHAR-604/605 — the parts behind the total.
  operatorPriceAmount: number | null;
  operatorPriceCcy: string | null;
  marginGbp: number | null;
  // SHAR-608 — derived server-side from the same breakdown the form uses, so a
  // figure in this list cannot contradict the one shown while quoting.
  operatorPriceGbp?: number;
  processingFeeGbp?: number;
  /** PO, 29 Sep — the customer's total (hold + balance), as the apps show it. */
  displayTotalGbp?: number;
  preAuthPaidGbp?: number;
  /** SHAR-994 — the fixed fee inside the hold (breakdown input/echo). */
  preAuthFixedFeeGbp?: number;
  /** SHAR-994 — the fee added to this quote's total. Not itemised on screen. */
  fixedFeeGbp?: number;
  preAuthExpired?: boolean;
  remainingGbp?: number;
  expiryDate: string | null;
  status: CharterQuoteStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  /** SHAR-614 — what differs from the request; absent from an API before SHAR-614. */
  alternatives?: QuoteAlternatives;
  // SHAR-115/117 — the booking made from this quote, with its passenger
  // manifest. Null on every quote that has not been booked, which is what the
  // page keys "booked by the user" off.
  booking: CharterBookingSummary | null;
};

export type CharterBookingStatus =
  | 'pre_approved'
  | 'confirmed'
  // SHAR-792 / SHAR-94 — DERIVED, never stored: a confirmed booking whose
  // arrival has elapsed. The API computes it on read (charterEffectiveStatus),
  // the same way ShareJet bookings have worked since SHAR-413. It can arrive in
  // `status` but must never be SENT as one - use `rawStatus` for anything that
  // writes.
  | 'complete'
  | 'cancelled';
export type CharterPaymentMethod = 'card' | 'wire_transfer' | 'pre_auth';

export type CharterPassenger = {
  id: string;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  // SHAR-281 — "our team will reach out": the passport details may follow later.
  passportPending: boolean;
  passportNumber: string | null;
  issuingCountry: string | null;
  passportExpiry: string | null;
  passportPhotoUrl: string | null;
  extraDocUrls: string[];
};

/** SHAR-115 — the booking as it appears nested under its quote. */
export type CharterBookingSummary = {
  id: string;
  reference: string | null;
  status: CharterBookingStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  quoteId: string;
  paymentMethod: CharterPaymentMethod;
  totalGbp: number;
  preAuthAppliedGbp: number | null;
  remainingGbp: number;
  wirePaidAt: string | null;
  /** Promised but not received — a wire booking nobody has confirmed yet. */
  awaitingWireTransfer: boolean;
  /** SHAR-115 — the user has paid the full quote price, not merely booked. */
  confirmedByUser: boolean;
  createdAt: string;
  passengers: CharterPassenger[];
};

/** SHAR-118 — one row of the charter bookings list. */
export type CharterBookingRow = {
  /** SHAR-1192 — on the airport's clock; absent from an API before SHAR-1189. */
  departureZoned?: Zoned | null;
  arrivalZoned?: Zoned | null;
  id: string;
  reference: string | null;
  requestId: string;
  requestReference: string;
  flightNumber: string | null;
  /** Display status — may be the derived 'complete' (SHAR-792). */
  status: CharterBookingStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  /** SHAR-792 — the STORED status, for the edit form and its guards. */
  rawStatus: Exclude<CharterBookingStatus, 'complete'>;
  paymentMethod: CharterPaymentMethod;
  /** SHAR-130 — when the transfer landed; null while it is still promised. */
  wirePaidAt: string | null;
  awaitingWireTransfer: boolean;
  /** SHAR-1028/131 - the "Wire transfer required" ad-hoc label, until an admin removes it or the payment is marked received. */
  adhocAwaitingTransfer: boolean;
  /** SHAR-1027 - the "Passport needed" ad-hoc label, as on ShareJet bookings, until an admin removes it. */
  adhocPassportNeeded: boolean;
  user: { id: string; name: string | null; email: string; phone: string | null; accountDeletedAt?: string | null } | null; // SHAR-1201
  tailNumber: string | null;
  operatorName: string | null;
  modelName: string | null;
  category: AircraftCategory | null;
  seatingCapacity: number | null;
  passengersCount: number;
  originCode: string;
  destCode: string;
  originCity: string | null;
  destCity: string | null;
  originCountry: string | null;
  destCountry: string | null;
  /** SHAR-735 — the full airport name, as the ShareJet itinerary block shows. */
  originName: string | null;
  destName: string | null;
  departureDateTime: string;
  arrivalDateTime: string | null;
  /** SHAR-735 — the estimated flight time the itinerary block shows. */
  estFlightTimeHours: number | null;
  /**
   * SHAR-738 — what the QUOTE says, before any admin correction, and whether a
   * correction exists. The fields above already carry the corrected values.
   */
  quotedAircraft: {
    operatorName: string | null;
    tailNumber: string | null;
    modelName: string | null;
    category: AircraftCategory | null;
    seatingCapacity: number | null;
  };
  aircraftEdited: boolean;
  /**
   * SHAR-735 — the same for the itinerary: what the quote says, and whether an
   * admin has corrected it on this booking. The fields above carry the
   * corrected route and departure.
   */
  quotedItinerary: {
    departureZoned?: Zoned | null;
    originCode: string;
    destCode: string;
    departureDateTime: string;
  };
  itineraryEdited: boolean;
  totalGbp: number;
  // SHAR-118 — operator price in the currency it was agreed in, plus its GBP
  // equivalent (absent on the detail payload, which carries the same figure in
  // `revenue`).
  operatorPriceAmount: number | null;
  operatorPriceCcy: string | null;
  operatorPriceGbp?: number | null;
  marginGbp: number | null;
  createdAt: string;
};

export type CharterBookingList = {
  data: CharterBookingRow[];
  total: number;
  page: number;
  pageSize: number;
  counters: {
    total: number;
    created: number;
    pre_approved: number;
    confirmed: number;
    complete: number;
    cancelled: number;
    /** SHAR-1027 — bookings carrying the "Passport needed" label. */
    passportNeeded: number;
    /** SHAR-131 — bookings carrying the "Wire transfer required" label. */
    wireTransfer?: number;
  };
};

export type CharterFbo = { name: string | null; address: string | null; phone: string | null };

/** SHAR-120/122/123 — flight information, passengers and revenue in one payload. */
// The list row's operatorPriceGbp is not on the detail (its revenue block has it).
export type CharterBookingDetail = Omit<CharterBookingRow, 'operatorPriceGbp'> & {
  /** Cancelled with money taken: the admin refunds it outside the app. */
  refundOutsideAppAt?: string | null;
  /** A cancellation whose transfer hold the payment provider would not release. */
  holdReleaseFailedAt?: string | null;
  holdReleaseFailure?: string | null;
  fbo: { departure?: CharterFbo; arrival?: CharterFbo } | null;
  passengers: CharterPassenger[];
  revenue: {
    operatorPriceGbp: number | null;
    operatorPriceAmount: number | null;
    operatorPriceCcy: string | null;
    marginGbp: number | null;
    processingFeeGbp: number | null;
    /** SHAR-1374 — the fixed fee(s) the customer paid: the quote's and the pre-auth hold's. */
    fixedFeeGbp: number | null;
    totalGbp: number;
    // SHAR-123 — null with preAuthExpired set means the hold lapsed and no
    // money was ever taken; an amount here would imply otherwise.
    preAuthPaidGbp: number | null;
    preAuthExpired: boolean;
    remainingGbp: number;
    remainingPaid: boolean;
  };
};

export type CharterPassengerInput = {
  fullName: string;
  dateOfBirth: string;
  gender: string;
  nationality: string;
  passportPending?: boolean;
  passportNumber?: string;
  issuingCountry?: string;
  passportExpiry?: string;
  /**
   * SHAR-781 — `null` DELETES the photo, `undefined` leaves it alone. The API
   * has always honoured the difference (charters.service: `!== undefined`);
   * only the admin form never had a way to say null.
   */
  passportPhotoUrl?: string | null;
  extraDocUrls?: string[];
};

export type CharterRequestRow = {
  /** SHAR-1192 — on the airport's clock; absent from an API before SHAR-1189. */
  departureZoned?: Zoned | null;
  id: string;
  status: CharterRequestStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  originCode: string;
  destCode: string;
  originCity: string | null;
  destCity: string | null;
  // SHAR-584/585 — the API resolves the stored code to ICAO and adds the country.
  originIata: string | null;
  destIata: string | null;
  originCountry: string | null;
  destCountry: string | null;
  departureDate: string;
  departureTime: string;
  seats: number;
  modelName: string | null;
  // SHAR-582/581 — model, tail number and operator are separate values.
  tailNumber: string | null;
  operatorName: string | null;
  category: string | null;
  indicativePriceGbp: number | null;
  /** SHAR-580 — price of the booked quote; null until one is booked. */
  soldPriceGbp: number | null;
  /** SHAR-117 — the booking made from the booked quote; null until there is one. */
  bookingId: string | null;
  bookingReference: string | null;
  preAuthAmountGbp: number | null;
  /** SHAR-994 — the fixed fee inside the hold above. */
  preAuthFixedFeeGbp?: number | null;
  preAuthExpired: boolean;
  /** SHAR-1092 — the hold lapsed while the request was open: "Payment required". */
  paymentRequired?: boolean;
  /** SHAR-1105 — the same label as an admin ad-hoc status, until an admin removes it. */
  adhocPaymentRequired?: boolean;
  /** SHAR-1116 — the customer asked for new quotes and none have been sent since. */
  adhocQuoteRefresh?: boolean;
  /** SHAR-1106-1109 — what became of the pre-auth hold, when, and why it was released. */
  preAuthState?: 'held' | 'captured' | 'expired' | 'released' | null;
  preAuthStateAt?: string | null;
  preAuthReleaseReason?: string | null;
  quotesCount: number;
  user: { name: string; email: string; accountDeletedAt?: string | null } | null; // SHAR-1201
};

export type CharterCounts = {
  total: number;
  pending: number;
  quotes_received: number;
  booked: number;
  cancelled: number;
  /** SHAR-1105 — requests carrying the "Payment required" label. */
  paymentRequired?: number;
  /** SHAR-1116 — requests carrying the "Quote refresh" label. */
  quoteRefresh?: number;
};

export type CharterRequestList = {
  data: CharterRequestRow[];
  total: number;
  page: number;
  pageSize: number;
  counts: CharterCounts;
};

/**
 * SHAR-995 — the revenue behind a charter request, in the same terms as the
 * ShareJet booking block and without its seat fields: a charter books the whole
 * aircraft. SHAR-1059 - always the request's own indicative calculation, never
 * the booking made from it. `source` says whether it is the split frozen when
 * the request was created ('request') or, for a request older than that,
 * recomputed from its indicative price with today's settings ('indicative').
 */
export type CharterRequestRevenue = {
  aircraftMgavGbp: number | null; // SHAR-1180 — null without an exchange rate
  operatorRevenueGbp: number;
  processingFeePct: number;
  processingCommissionGbp: number;
  shareJetMarginGbp: number;
  totalRevenueGbp: number;
  /** SHAR-1374 — the fixed fee the customer paid with the pre-authorisation hold (0 when none was taken). */
  fixedFeeGbp?: number;
  /** What the customer was charged: the revenue above plus the fixed fee. */
  customerTotalGbp?: number;
  source: 'request' | 'indicative';
};

export type CharterRequestDetail = CharterRequestRow & {
  /** A cancellation whose deposit hold the payment provider would not release. */
  preAuthReleaseFailedAt?: string | null;
  preAuthReleaseFailure?: string | null;
  revenue?: CharterRequestRevenue;
  passengersCount: number;
  user: { name: string; email: string; phone: string; accountDeletedAt?: string | null } | null; // SHAR-1201
  // The aircraft's operator as an object, kept for the quote form's selector.
  // operatorName on the row is what the Request block displays (SHAR-581).
  operator: { id: string; name: string } | null;
  quotes: CharterQuote[];
};

// SHAR-1211 — the API's QuoteDto, field for field (SHAR-604/605: operator price,
// currency and margin make the server derive priceGbp).
export type CharterQuoteInput = CharterQuoteBody;

// SHAR-604 — the dynamic calculation shown under the price fields.
export type CharterQuoteBreakdown = {
  operatorPriceAmount: number | null;
  operatorPriceCcy: string | null;
  operatorPriceGbp: number;
  marginGbp: number;
  // SHAR-603 — estimated flight time for the quoted route and aircraft, or null
  // when the route or the aircraft cannot supply one.
  flightDurationHours: number | null;
  // SHAR-604 — what the house rate gives on this operator price, so the form can
  // seed the margin field without knowing the percentage itself.
  suggestedMarginGbp: number;
  charterMarginPct: number;
  processingFeePct: number;
  processingFeeGbp: number;
  totalGbp: number;
  /** The fare part of the hold, deducted from the total (SHAR-994). */
  preAuthPaidGbp: number;
  /** PO, 29 Sep — the hold as the user paid it, fee included; the figure shown. */
  preAuthChargedGbp?: number;
  /** PO, 29 Sep — the customer's total: hold + remaining (quote price with no hold). */
  displayTotalGbp?: number;
  preAuthExpired: boolean;
  remainingGbp: number;
};

// ─── Platform settings (SHAR-321/322/318) ─────────────────────────────
export type AppSettings = {
  id: string;
  creationLeadTimeMinDays: number;
  creationLeadTimeMaxDays: number;
  mgavDeadlineDays: number;
  processingFeePct: number;
  // SHAR-604/917 — ShareJet's margin, percent of the operator price. One number
  // for the whole platform: it prices charter quotes and ShareJet seats alike.
  sharejetMarginPct: number;
  // SHAR-926 — the fixed fee added to the final sum of every transaction, in GBP.
  fixedFeeGbp: number;
  // SHAR-720 — charters have their own minimum lead time, in hours; ShareJet
  // bookings keep the two day-based fields above.
  charterLeadTimeMinHours: number;
  updatedAt: string;
};

export type Paginated<T> = {
  total: number;
  page: number;
  pageSize: number;
  items: T[];
};

// ─── Bookings (SHAR-46 / SHAR-27 / SHAR-62) ────────────────────────────
export const BOOKING_STATUSES = [
  'pending',
  'pre_approved',
  'confirmed',
  'cancelled',
  'complete', // SHAR-413 — computed (confirmed + arrival past)
] as const;

export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export type AdminBookingRow = {
  /** SHAR-1192 — on the airport's clock; absent from an API before SHAR-1189. */
  departureZoned?: Zoned | null;
  id: string;
  flightNumber: string | null;
  status: BookingStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  originCode: string;
  destCode: string;
  originIcao: string;
  destIcao: string;
  category: string;
  operator: string | null;
  departureDate: string; // YYYY-MM-DD
  departureTime: string; // HH:mm
  seatsBooked: number;
  seatsTotal: number;
  seatPriceGbp: number;
  totalGbp: number;
  modelName: string | null;
  tailNumber: string | null;
  user: { id: string; email: string; name: string; accountDeletedAt?: string | null } | null; // SHAR-1201
  mgavOverdue: boolean;
  newSeats: boolean;
  passportNeeded: boolean; // SHAR-376
  notPaid: boolean; // SHAR-163
  mgavProgress: AdminBookingMgavProgress | null; // null in the spec (SHAR-1211 pin)
  createdAt: string;
};

export type BookingWhen = 'upcoming' | 'past';

export type BookingCounts = {
  total: number;
  confirmed: number;
  pre_approved: number;
  pending: number;
  cancelled: number;
  mgavOverdue: number;
  newSeats: number;
  notPaid: number; // SHAR-163
  passportNeeded: number; // SHAR-451
};

export type AdminBookingPlace = {
  code: string;
  port: string;
  city: string;
  country: string;
};

export type AdminBookingPassenger = {
  id: string;
  ownerUserId: string; // SHAR-48 — which seat-holder this passenger belongs to
  fullName: string;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string;
  passportNumber: string | null;
  issuingCountry: string | null;
  passportExpiry: string | null;
  passportPending: boolean;
  passportPhotoUrl: string | null;
  extraDocUrls: string[];
};

// SHAR-107 — booking revenue breakdown + per-user payment status.
export type BookingRevenue = {
  aircraftMgavGbp: number | null; // SHAR-1180 — null without an exchange rate
  seatsBooked: number;
  operatorRevenueGbp: number;
  operatorRevenueCurrency: string | null;
  operatorRevenueInCurrency: number | null;
  processingFeePct: number;
  processingCommissionGbp: number;
  shareJetMarginGbp: number;
  totalRevenueGbp: number;
  /** SHAR-1374 — the fixed fee inside the users' payments (not revenue), and revenue plus that fee. */
  fixedFeeGbp: number;
  customerTotalGbp: number;
  /** SHAR-1091 — a cancelled booking's history: what was held when it was cancelled. NOT revenue. */
  atCancellation?: { seats: number; heldGbp: number } | null;
};

/** SHAR-1091 — what a seat-holder held when cancelled, and what that money was. History, not revenue. */
export type AtCancellation = { seats: number; heldGbp: number; kind: 'charge' | 'hold' | 'lapsed' | 'none' | 'refunded' };

export type BookingUserPayment = {
  userId: string;
  name: string;
  pricePerSeatGbp: number;
  seats: number;
  totalPaidGbp: number;
  // SHAR-593 — a cancelled holder keeps their row here, marked, with 'released'
  // where a hold was let go (rather than 'not paid', which reads as never paid).
  cancelled?: boolean;
  /** SHAR-1091 — history at cancellation (null when nothing of theirs was cancelled). */
  atCancellation?: AtCancellation | null;
  status: 'paid' | 'pre_auth' | 'not_paid' | 'refunded' | 'released' | 'payment_required'; // SHAR-107/593/159
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  // SHAR-495 — every payment attempt for this user, so removing the standalone
  // Payments block loses nothing (kind / card / failure reason / date).
  attempts: AdminBookingPayment[];
};

// SHAR-495 — a payment whose payer is no longer a seat-holder (cancelled and
// refunded users), shown separately so it stays visible.
export type BookingUnattributedPayment = AdminBookingPayment & { userId: string | null };

// SHAR-48 — a booking seat-holder (creator or joiner) with contact details.
export type BookingParticipant = {
  id: string;
  name: string;
  email: string;
  phone: string;
  isCreator: boolean;
  /** SHAR-1201 — when the customer deleted the account (ISO); null for a live account. */
  accountDeletedAt?: string | null;
  /** Seats this holder holds (open participation) - the passenger limit. */
  seats?: number;
};

// SHAR-466 — a user who cancelled or was cancelled from a booking.
export type CancelledUser = {
  id: string;
  name: string;
  email: string;
  /** SHAR-1201 — when the customer deleted the account (ISO); null for a live account. */
  accountDeletedAt?: string | null;
  phone: string;
  // SHAR-494 — the cancelled user's passengers, preserved by the soft-remove.
  // Empty for users cancelled before that change, whose rows were hard-deleted.
  passengers?: (AdminBookingPassenger & { removedAt?: string | null })[];
};

export type AdminBookingPayment = {
  id: string;
  kind: 'preauth' | 'charge';
  // released: a hold let go on a cancellation; captured_hold: a hold taken as a
  // charge (the charge is its own row); voided: either, on older rows.
  status: 'authorized' | 'captured' | 'failed' | 'released' | 'captured_hold' | 'voided' | 'refunded' | 'expired';
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  amountGbp: number;
  cardLast4: string | null;
  /** SHAR-1289 — the payment provider's (WorldPay) reference of this payment; absent or null for older and demo payments. */
  providerRef?: string | null;
  failureReason: string | null;
  /** Captured, and its booking or seats cancelled: the admin refunds it outside the app. */
  refundOutsideAppAt?: string | null;
  /** A cancellation whose hold the payment provider would not release. */
  releaseFailedAt?: string | null;
  releaseFailure?: string | null;
  createdAt: string;
};

export type AdminBookingInvoice = {
  id: string;
  kind: 'preauth' | 'final';
  number: string;
  amountGbp: number;
  createdAt: string;
  /** SHAR-1011 — the buyer this invoice belongs to; a shared booking has several. */
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  /** SHAR-1009 — signed link to the PDF, as on the charter blocks. */
  downloadUrl?: string | null;
  /** SHAR-1098 — set when a pre-auth hold was released (SHAR-481); null otherwise. */
  releasedAt?: string | null;
  releaseReason?: string | null;
  /** SHAR-1097 — a pre-auth whose hold was captured when the booking was confirmed. */
  captured?: boolean;
};

// mgavProgress shape isn't strictly fixed by the contract — render defensively.
export type AdminBookingMgavProgress = {
  fraction?: number;
  percent?: number;
  seatsBooked?: number;
  minSeatsRequired?: number;
  seatsRequired?: number;
  met?: boolean;
  deadline?: string;
  [key: string]: unknown;
};

// FBO (fixed-base operator) — departure/arrival ground handler details.
export type AdminBookingFbo = {
  name: string | null;
  address: string | null;
  phone: string | null;
} | null;

export type AdminBookingDetail = {
  /** SHAR-1192 — on the airport's clock; absent from an API before SHAR-1189. */
  departureZoned?: Zoned | null;
  arrivalZoned?: Zoned | null;
  id: string;
  status: BookingStatus;
  /** SHAR-1184 — the API's display name for `status`; absent from an older API. */
  statusLabel?: string;
  /** SHAR-1202 — the statuses an admin may move this booking to now (absent on an older API). */
  allowedStatusChanges?: BookingStatus[];
  flightNumber: string | null;
  mgavOverdue: boolean;
  newSeats: boolean;
  passportNeeded: boolean; // SHAR-376
  notPaid: boolean; // SHAR-163
  aircraft: {
    modelName: string;
    tailNumber: string | null;
    category: AircraftCategory;
    seats: number;
    operator: { id: string; name: string };
  };
  user: { id: string; email: string; firstName: string; lastName: string; accountDeletedAt?: string | null }; // SHAR-1201
  route: { origin: AdminBookingPlace; dest: AdminBookingPlace };
  departureDate: string;
  departureTime: string;
  arrivalDate: string | null;
  arrivalTime: string | null;
  estFlightTimeHours: number | null;
  seatsBooked: number;
  seatsTotal: number;
  seatPriceGbp: number;
  totalGbp: number;
  mgavProgress: AdminBookingMgavProgress | null; // null in the spec (SHAR-1211 pin)
  departureFbo: AdminBookingFbo;
  arrivalFbo: AdminBookingFbo;
  participants: BookingParticipant[]; // SHAR-48
  cancelledUsers?: CancelledUser[]; // SHAR-466
  revenue: BookingRevenue; // SHAR-107
  userPayments: BookingUserPayment[]; // SHAR-107
  unattributedPayments: BookingUnattributedPayment[]; // SHAR-495
  passengers: AdminBookingPassenger[];
  payments: AdminBookingPayment[];
  invoices: AdminBookingInvoice[];
};
